
import type { ChatMessage } from '@/use/use.flowiseQuestionStream'
import Modal from '@/components/Modal.vue'
import MenuIcon from '@/components/Icons'
import { ref, computed, watch, nextTick, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import CloseButton from '@/components/ModalCloseButton.vue'
import { AttensiCard } from '@attensi/materia'

export default {
  components: {
    Modal,
    MenuIcon,
    CloseButton,
    AttensiCard,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    userAvatar: {
      type: String,
      required: true,
    },
    questionSuggestions: {
      type: Array as PropType<string[]>,
      required: true,
    },
    messages: {
      type: Array as PropType<ChatMessage[]>,
      required: true,
    },
    isPendingAnswer: {
      type: Boolean,
      required: true,
    },
    signedAiDisclaimer: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close', 'submit'],
  setup(props: any, { emit }: any) {
    const { t } = useI18n()
    const question = ref('')
    const messagesScrollContainerRef = ref<HTMLDivElement | null>(null)
    const textAreaRef = ref<HTMLTextAreaElement | null>(null)

    const questionMaxLength = 1000
    const isQuestionMaxLength = computed(
      () => question.value.length >= questionMaxLength
    )
    const isChatEmpty = computed(() => props.messages.length === 0)

    const isLastMessage = (index: number) => {
      return props.messages.length - 1 === index
    }

    const isQuestionSuggestions = computed(
      () =>
        props.questionSuggestions !== undefined &&
        props.questionSuggestions.length > 0
    )

    // Keep chat scrolled to bottom on incoming or outgoing message
    watch(
      () => props.messages,
      async () => {
        await nextTick()
        if (!messagesScrollContainerRef.value) return

        messagesScrollContainerRef.value.scrollTop =
          messagesScrollContainerRef.value.scrollHeight
      },
      { deep: true }
    )

    // Ensure scroll is at bottom when reopening the modal
    watch(
      () => props.isOpen,
      async (isOpen: boolean) => {
        if (!isOpen) return
        await nextTick()
        if (!messagesScrollContainerRef.value) return

        messagesScrollContainerRef.value.scrollTop =
          messagesScrollContainerRef.value.scrollHeight
      }
    )

    // Let chat input dynamically increase and decrease in height as the input is filled up
    const adjustTextAreaHeight = () => {
      if (!textAreaRef.value?.style) return

      // Allow text area to shrink in height text content is reduced
      textAreaRef.value.style.height = 'auto'
      // Allow text area to grow in height as text content is increased
      textAreaRef.value.style.height = `${textAreaRef.value.scrollHeight}px`
      textAreaRef.value.rows = 1
    }

    watch(
      () => question.value,
      () => {
        nextTick(() => {
          adjustTextAreaHeight()
        })
      }
    )

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()
        handleSubmitQuestion()
      }
    }

    const handleInput = (event: Event) => {
      const target = event.target as HTMLTextAreaElement
      question.value = target.value
      adjustTextAreaHeight()
    }

    const handleSubmitQuestion = () => {
      const trimmedQuestion = question.value.trim()
      const isNoQuestionPresent = trimmedQuestion.length === 0

      if (props.isPendingAnswer || isNoQuestionPresent) return

      emit('submit', trimmedQuestion)
      question.value = ''
    }

    const onClickSuggestion = (suggestionQuestion: string) => {
      emit('submit', suggestionQuestion)
    }

    return {
      t,
      question,
      messagesScrollContainerRef,
      textAreaRef,
      isChatEmpty,
      isLastMessage,
      isQuestionSuggestions,
      handleKeyDown,
      handleInput,
      handleSubmitQuestion,
      onClickSuggestion,
      questionMaxLength,
      isQuestionMaxLength,
    }
  },
}
